import axios from 'axios'
import type { InternalAxiosRequestConfig } from 'axios'

export const apiClient = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
  // 30 сек — верхняя граница на любой запрос. Защищает от зависаний при
  // цепочке retry (стресс-тест 21.04 показал висящий 18-мин запрос из-за
  // неограниченного retry-лупа без timeout).
  timeout: 30_000,
})

// Очередь запросов, ожидающих завершения рефреша
let isRefreshing = false
let failedQueue: Array<{ resolve: () => void; reject: (e: unknown) => void }> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve()))
  failedQueue = []
}

const clearAuthState = async () => {
  const { useAuthStore } = await import('../../modules/auth/authStore')
  useAuthStore.setState({ user: null, isAuthenticated: false, isAuthChecked: true })
}

/**
 * Retry только для идемпотентных запросов (GET/HEAD) на транзиентных
 * ошибках (деплой API: 3-5 сек окно между рестартами; сеть моргнула).
 *
 * ПОЧЕМУ ТОЛЬКО GET: мутации (POST/PATCH/PUT/DELETE) при 502/504 могут
 * создать дубли (запрос дошёл до api, был обработан, ответ потерян →
 * retry создаст второй экземпляр). Для CRM это критично (дубли квот,
 * дубли аудит-логов). POST-мутации пусть падают — пользователь увидит
 * ошибку и повторит осознанно, если это было нужно.
 *
 * Не ретраим: 4xx (валидация/403/404), вход/обновление/выход (чтобы не
 * дублировать логин/refresh), не-GET методы, и если счётчик уже ≥ RETRY_LIMIT.
 */
// Общий потолок попыток для ВСЕХ retry-цепочек на одном запросе
// (transient 502/503/504 + 429). Раньше счётчик был общий, но блоки
// независимо могли уложить до 14с + 15с = 29с молчания пользователю.
// Теперь cap=3 — даже если попался и 502, и 429 на одном запросе,
// больше 3 попыток не сделаем, паузы максимум суммарно ~8 секунд.
const RETRY_LIMIT = 3
// 1+2+4 = 7с суммарного молчания при цепочке 502/503/504 (правка 24.05.2026).
// Раньше было 2+4+8 = 14с — слишком долго для пользователя, теперь короче.
const RETRY_DELAYS_MS = [1000, 2000, 4000]
// Максимум для одной паузы при 429 — иначе сервер может попросить ждать
// «60 секунд», и UI зависнет. Если bucket переполнен надолго — пусть
// клиент увидит ошибку и сам решит, а не висит в крутилке.
const MAX_429_DELAY_S = 8

type RetryableConfig = InternalAxiosRequestConfig & {
  __retryCount?: number
  /** Запрос уже повторён после обновления сессии — второй раз не обновляем. */
  __authRetried?: boolean
}

function isTransient(status?: number, code?: string): boolean {
  if (code === 'ERR_NETWORK' || code === 'ECONNABORTED') return true
  if (status === 502 || status === 503 || status === 504) return true
  return false
}

/**
 * Служебные адреса самого входа: их не повторяем и на их 401 не обновляем сессию
 * (иначе вход и обновление зациклились бы сами на себя).
 *
 * Раньше сюда попадал любой адрес с «/auth/», в том числе проверка сессии
 * GET /auth/me. Через 15 минут простоя короткий токен истекал, /auth/me
 * получал 401 без попытки обновления, и человека выбрасывало на вход при живой
 * 7-дневной сессии. Обрыв связи на /auth/me тоже не повторялся (19.10.2026,
 * вход с телефона через VPN).
 */
export function isAuthFlowUrl(url: string): boolean {
  return /\/auth\/(login|refresh|logout|register)(\?|$)/.test(url)
}

function isIdempotent(method?: string): boolean {
  const m = (method ?? 'get').toLowerCase()
  return m === 'get' || m === 'head'
}

apiClient.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!axios.isAxiosError(error)) return Promise.reject(error)

    const status = error.response?.status
    const code = error.code
    const url = error.config?.url ?? ''
    const config = error.config as RetryableConfig | undefined

    // 1) Retry на транзиентных ошибках и 429 — ТОЛЬКО для GET/HEAD
    // (идемпотентные). Мутации не ретраим, чтобы не было дублей.
    //
    // Единый счётчик __retryCount общий для обоих кейсов (transient + 429):
    // раньше блоки были независимы и могли уложить до 29с молчания, если
    // на одном запросе случилось и 502, и 429. Теперь суммарно максимум
    // 3 попытки с короткими паузами (1.5/3/5с для transient, до 8с для 429).
    if (config && !isAuthFlowUrl(url) && isIdempotent(config.method)) {
      const attempt = config.__retryCount ?? 0
      let delay: number | null = null

      // Проверку сессии по таймауту не повторяем: при «висящей» сети (VPN) 4 попытки по
      // 30 с держали заставку около двух минут, прежде чем показать «Нет связи»
      // (аудит 20.10.2026). Обрыв связи (ERR_NETWORK) повторяем — он короткий.
      const sessionTimeout = code === 'ECONNABORTED' && /\/auth\/me(\?|$)/.test(url)
      if (isTransient(status, code) && !sessionTimeout && attempt < RETRY_LIMIT) {
        delay = RETRY_DELAYS_MS[attempt] ?? 1000
      } else if (status === 429 && attempt < RETRY_LIMIT) {
        // Бэк отдаёт retryAfterSeconds в error.message envelope; запасной
        // вариант — стандартный заголовок Retry-After. Капаем
        // MAX_429_DELAY_S чтобы UI не висел дольше.
        const data = error.response?.data as { error?: { retryAfterSeconds?: number } } | undefined
        const fromBody = data?.error?.retryAfterSeconds
        const fromHeader = Number(error.response?.headers?.['retry-after'])
        const seconds = fromBody ?? (Number.isFinite(fromHeader) ? fromHeader : 2)
        delay = Math.min(MAX_429_DELAY_S, Math.max(1, seconds)) * 1000
      }

      if (delay !== null) {
        config.__retryCount = attempt + 1
        await new Promise((r) => setTimeout(r, delay))
        return apiClient(config)
      }
    }

    // 2) 401 → пробуем /auth/refresh, при успехе повторяем запрос, при неудаче — разлогин
    if (status !== 401 || isAuthFlowUrl(url)) return Promise.reject(error)

    const originalConfig = error.config! as RetryableConfig

    // Запрос уже повторяли после обновления сессии, а сервер снова ответил 401: новая
    // кука не легла (браузер её не принял). Повторное обновление пошло бы по кругу и
    // съело бы лимит входа всему офису (аудит 20.10.2026) — выходим.
    if (originalConfig.__authRetried) {
      await clearAuthState()
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      })
        .then(() => apiClient({ ...originalConfig, __authRetried: true } as RetryableConfig))
        .catch((e) => Promise.reject(e))
    }

    isRefreshing = true
    try {
      await apiClient.post('/auth/refresh')
      processQueue(null)
      return apiClient({ ...originalConfig, __authRetried: true } as RetryableConfig)
    } catch (refreshError) {
      processQueue(refreshError)
      // Разлогиниваем ТОЛЬКО если рефреш отклонён именно как невалидная
      // сессия (401). 429 (rate-limit под нагрузкой), сеть, 5xx — транзиентные:
      // сессия ещё жива, выкидывать пользователя нельзя. Запрос просто упадёт.
      const refreshStatus = axios.isAxiosError(refreshError)
        ? refreshError.response?.status
        : undefined
      if (refreshStatus === 401) await clearAuthState()
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  },
)
